import { Injectable } from '@angular/core';

import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { EnvironmentService, EventService, ExportActions, GridActions, GridService, ListService, fromList } from '@core/services';
import { currentTimeInSeconds } from '@util/list';

import { SummaryRow, SummaryRowText } from '#types/grid';
import { InventoryInquiryResponseRow, InventoryItemDetailResponseRow, InventoryItemLotResponseRow } from '#types/inventory';
import { MultipleListOfValuesResponse } from '#types/list';

import { AuthActions } from '@auth/state';
import { CONSTANTS } from '@inventory/constants';
import { InventoryService } from '@inventory/services';

import * as InventoryActions from './inventory.actions';

@Injectable()
export class InventoryEffects {
  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private actions$: Actions,
    private store: Store,
    private environmentService: EnvironmentService,
    private listService: ListService,
    private inventoryService: InventoryService,
    private gridService: GridService,
    private translocoService: TranslocoService,
    private eventService: EventService
  ) {}

  RequestLists$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(InventoryActions.RequestLists),
        concatLatestFrom(() => [this.store.select(fromList.selectInventoryLists)]),
        map(([_, lists]) => {
          let lastUpdated = -1;

          for (const list of Object.keys(lists)) {
            // skip parameterized lists
            if (lists[list] && !Array.isArray(lists[list].data)) {
              continue;
            }

            if (lists[list]) {
              lastUpdated = Math.max(lastUpdated, currentTimeInSeconds() - (lists[list].updated ?? 0));
            }
          }

          if (lastUpdated === -1 || lastUpdated > this.environmentService.settings.secondsToCacheLists) {
            this.store.dispatch(InventoryActions.RequestInventoryLists());
          }
        })
      );
    },
    { dispatch: false }
  );

  requestInventoryLists$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(InventoryActions.RequestInventoryLists),
      exhaustMap(() => {
        return this.listService.getListOfValues<MultipleListOfValuesResponse>('inventory').pipe(
          this.eventService.indicate(CONSTANTS.LIST_ALL),
          map((lists) => InventoryActions.RequestInventoryListsSuccess({ lists })),
          catchError((error) => of(InventoryActions.RequestInventoryListsFailure(error)))
        );
      })
    );
  });

  Initialize$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(InventoryActions.InquiryInitialize, InventoryActions.ItemDetailInitialize, InventoryActions.LotDetailInitialize),
      map((screen) => AuthActions.ChangeScreen(screen))
    );
  });

  InquiryRequestData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        InventoryActions.InquiryRequestData,
        InventoryActions.InquiryRequestDataWithParameters,
        InventoryActions.InquiryRequestDataWithFilters
      ),
      concatLatestFrom(({ id }) => [
        this.gridService.filters$(id),
        this.gridService.page$(id),
        this.gridService.rows$(id),
        this.gridService.sortsObject$(id),
      ]),
      exhaustMap(([{ id }, filters, page, rows, sorts]) =>
        this.inventoryService.inventoryInquiry({ filters, page, rows, sorts }).pipe(
          map((data) => InventoryActions.InquiryRequestDataSuccess({ id, data })),
          catchError((_error) => of(InventoryActions.InquiryRequestDataFailure({ id })))
        )
      )
    );
  });

  InquiryRequestDataForExport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(InventoryActions.InquiryRequestDataForExport),
      concatLatestFrom(({ id }) => [this.gridService.filters$(id), this.gridService.sortsObject$(id)]),
      exhaustMap(([{ id, format, rows }, filters, sorts]) =>
        this.inventoryService.inventoryInquiry({ filters, page: 1, rows, sorts }).pipe(
          map((data) => ExportActions.RequestDataForExportSuccess({ id, format, data })),
          catchError((error) => of(ExportActions.RequestDataForExportFailure({ id, error })))
        )
      )
    );
  });

  InquiryCalculateSummaryRow$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(InventoryActions.InquiryRequestDataSuccess, InventoryActions.InquiryRequestDataFailure),
        concatLatestFrom(({ id }) => [this.gridService.data$<InventoryInquiryResponseRow>(id)]),
        map(([{ id }, data]) => {
          if (!data || data.length === 0) {
            this.store.dispatch(GridActions.SetSummaryRow({ id, summaryRow: null }));
            return;
          }

          const summaryRowText: SummaryRowText = {
            item: this.translate('inventory.item_detail.grid.summary.total'),
          };

          this.store.dispatch(GridActions.SetSummaryRowText({ id, summaryRowText }));

          const summaryRow: SummaryRow = {
            qtytotal: 0,
            qtyalloc: 0,
            qtyavail: 0,
            totalweight_lbs: 0,
            totalweight_kgs: 0,
            totalweight: 0,
          };

          for (const row of data) {
            summaryRow.qtytotal += row.qtytotal ?? 0;
            summaryRow.qtyalloc += row.qtyalloc ?? 0;
            summaryRow.qtyavail += row.qtyavail ?? 0;
            summaryRow.totalweight_lbs += row.totalweight_lbs ?? 0;
            summaryRow.totalweight_kgs += row.totalweight_kgs ?? 0;
            summaryRow.totalweight += row.totalweight ?? 0;
          }

          this.store.dispatch(GridActions.SetSummaryRow({ id, summaryRow }));
        })
      );
    },
    { dispatch: false }
  );

  DetailItemDetailRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        InventoryActions.ItemDetailRequestData,
        InventoryActions.ItemDetailRequestDataWithParameters,
        InventoryActions.ItemDetailRequestDataWithFilters
      ),
      concatLatestFrom(({ id }) => [
        this.gridService.filters$(id),
        this.gridService.page$(id),
        this.gridService.rows$(id),
        this.gridService.sortsObject$(id),
      ]),
      exhaustMap(([{ id }, filters, page, rows, sorts]) =>
        this.inventoryService.inventoryItemDetail({ filters, page, rows, sorts }).pipe(
          map((data) => InventoryActions.ItemDetailRequestDataSuccess({ id, data })),
          catchError((_error) => of(InventoryActions.ItemDetailRequestDataFailure({ id, data: null })))
        )
      )
    );
  });

  ItemDetailRequestDataForExport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(InventoryActions.ItemDetailRequestDataForExport),
      concatLatestFrom(({ id }) => [this.gridService.filters$(id), this.gridService.sortsObject$(id)]),
      exhaustMap(([{ id, format, rows }, filters, sorts]) =>
        this.inventoryService.inventoryItemDetail({ filters, page: 1, rows, sorts }).pipe(
          map((data) => ExportActions.RequestDataForExportSuccess({ id, format, data })),
          catchError((error) => of(ExportActions.RequestDataForExportFailure({ id, error })))
        )
      )
    );
  });

  DetailItemDetailCalculateSummaryRow$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(InventoryActions.ItemDetailRequestDataSuccess, InventoryActions.ItemDetailRequestDataFailure),
        concatLatestFrom(({ id }) => [this.gridService.data$<InventoryItemDetailResponseRow>(id)]),
        map(([{ id }, data]) => {
          if (!data || data.length === 0) {
            this.store.dispatch(GridActions.SetSummaryRow({ id, summaryRow: null }));
            return;
          }

          const summaryRowText: SummaryRowText = {
            statusabbrev: this.translate('inventory.item_detail.grid.summary.total'),
          };

          this.store.dispatch(GridActions.SetSummaryRowText({ id, summaryRowText }));

          const summaryRow: SummaryRow = {
            quantity: 0,
            lipcount: 0,
            totalweight: 0,
            totalweight_kgs: 0,
          };

          for (const row of data) {
            if (row.status === 'CM') {
              continue;
            } else {
              summaryRow.quantity += row.quantity ?? 0;
              summaryRow.lipcount += row.lipcount ?? 0;
              summaryRow.totalweight += row.totalweight ?? 0;
              summaryRow.totalweight_kgs += row.totalweight_kgs ?? 0;
            }
          }

          this.store.dispatch(GridActions.SetSummaryRow({ id, summaryRow }));
        })
      );
    },
    { dispatch: false }
  );

  DetailLotDetailRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        InventoryActions.LotDetailRequestData,
        InventoryActions.LotDetailRequestDataWithParameters,
        InventoryActions.LotDetailRequestDataWithFilters
      ),
      concatLatestFrom(({ id }) => [
        this.gridService.filters$(id),
        this.gridService.page$(id),
        this.gridService.rows$(id),
        this.gridService.sortsObject$(id),
      ]),
      exhaustMap(([{ id }, filters, page, rows, sorts]) =>
        this.inventoryService.inventoryLotDetail({ filters, page, rows, sorts }).pipe(
          map((data) => InventoryActions.LotDetailRequestDataSuccess({ id, data })),
          catchError((_error) => of(InventoryActions.LotDetailRequestDataFailure({ id, data: null })))
        )
      )
    );
  });

  LotDetailRequestDataForExport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(InventoryActions.LotDetailRequestDataForExport),
      concatLatestFrom(({ id }) => [this.gridService.filters$(id), this.gridService.sortsObject$(id)]),
      exhaustMap(([{ id, format, rows }, filters, sorts]) =>
        this.inventoryService.inventoryLotDetail({ filters, page: 1, rows, sorts }).pipe(
          map((data) => ExportActions.RequestDataForExportSuccess({ id, format, data })),
          catchError((error) => of(ExportActions.RequestDataForExportFailure({ id, error })))
        )
      )
    );
  });

  DetailLotDetailCalculateSummaryRow$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(InventoryActions.LotDetailRequestDataSuccess, InventoryActions.LotDetailRequestDataFailure),
        concatLatestFrom(({ id }) => [this.gridService.data$<InventoryItemLotResponseRow>(id)]),
        map(([{ id }, data]) => {
          if (!data || data.length === 0) {
            this.store.dispatch(GridActions.SetSummaryRow({ id, summaryRow: null }));
            return;
          }

          const summaryRowText: SummaryRowText = {
            orderid: this.translate('inventory.item_detail.grid.summary.total'),
          };

          this.store.dispatch(GridActions.SetSummaryRowText({ id, summaryRowText }));

          const summaryRow: SummaryRow = {
            qtyorder: 0,
            qtypick: 0,
            qtycommit: 0,
          };

          for (const row of data) {
            summaryRow.qtyorder += row.qtyorder ?? 0;
            summaryRow.qtypick += row.qtypick ?? 0;
            summaryRow.qtycommit += row.qtycommit ?? 0;
          }

          this.store.dispatch(GridActions.SetSummaryRow({ id, summaryRow }));
        })
      );
    },
    { dispatch: false }
  );
}
